import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  FileText,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Mic,
  TrendingUp,
  Upload,
  Link as LinkIcon,
  ChevronRight,
  Volume2,
  Maximize2,
  Loader2,
  Pencil,
  AlertCircle,
  Plus,
  Wand2,
  Target,
  Send,
  Package,
  Flame,
  Award,
  Hash,
  Pause,
  Trash2,
  Moon,
  Sun,
  Lock,
  LogOut,
  User,
  CreditCard,
  Crown,
  RefreshCcw,
  Settings,
  History,
  Save,
  KeyRound,
  Code2,
  Play,
  BrainCircuit,
  Copy,
} from "lucide-react";
import axios from "axios";
import { getAllPackages } from "./companyPackages";

const DEFAULT_BACKEND_URL = "http://localhost:8000";

const normalizeBackendUrl = (value) => {
  const trimmed = (value || "").trim().replace(/\/+$/, "");
  if (!trimmed) return DEFAULT_BACKEND_URL;

  const candidate = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`;

  try {
    return new URL(candidate).toString().replace(/\/$/, "");
  } catch {
    console.warn(`[mockly] Invalid REACT_APP_BACKEND_URL: ${value}. Falling back to ${DEFAULT_BACKEND_URL}.`);
    return DEFAULT_BACKEND_URL;
  }
};

const BACKEND_URL = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "mockly_token";
const DRAFT_JOB_KEY = "mockly_draft_job";
const CURRENT_JOB_KEY = "mockly_current_job";
const CURRENT_QUESTION_KEY = "mockly_current_question";
const PRACTICE_STATE_KEY = "mockly_practice_state";
const THEME_KEY = "mockly_theme";
const CODING_DRAFT_PREFIX = "mockly_coding_draft_";

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return config;
});

const saveDraftJobData = (data) => localStorage.setItem(DRAFT_JOB_KEY, JSON.stringify(data));
const getDraftJobData = () => JSON.parse(localStorage.getItem(DRAFT_JOB_KEY) || "{}");
const clearDraftJobData = () => localStorage.removeItem(DRAFT_JOB_KEY);
const saveCurrentJobId = (jobId) => localStorage.setItem(CURRENT_JOB_KEY, jobId);
const getCurrentJobId = () => localStorage.getItem(CURRENT_JOB_KEY) || "";
const saveCurrentQuestionId = (questionId) => localStorage.setItem(CURRENT_QUESTION_KEY, questionId);
const getCurrentQuestionId = () => localStorage.getItem(CURRENT_QUESTION_KEY) || "";
const savePracticeState = (data) => localStorage.setItem(PRACTICE_STATE_KEY, JSON.stringify(data));
const getPracticeState = () => JSON.parse(localStorage.getItem(PRACTICE_STATE_KEY) || "{}");
const clearPracticeState = () => localStorage.removeItem(PRACTICE_STATE_KEY);
const clearAppState = () => {
  clearDraftJobData();
  localStorage.removeItem(CURRENT_JOB_KEY);
  localStorage.removeItem(CURRENT_QUESTION_KEY);
  clearPracticeState();
};

const getCodingDraftKey = (challengeId) => `${CODING_DRAFT_PREFIX}${challengeId}`;
const loadCodingDraft = (challengeId) => (challengeId ? localStorage.getItem(getCodingDraftKey(challengeId)) || "" : "");
const saveCodingDraft = (challengeId, code) => {
  if (!challengeId) return;
  localStorage.setItem(getCodingDraftKey(challengeId), code);
};

const copyText = async (value) => {
  if (!navigator?.clipboard?.writeText) {
    throw new Error("Clipboard access is unavailable in this browser");
  }
  await navigator.clipboard.writeText(value);
};

const getErrorDetail = (error) => error?.response?.data?.detail;

const getErrorMessage = (error, fallback = "Something went wrong") => {
  if (typeof getErrorDetail(error) === "string") return getErrorDetail(error);
  if (typeof getErrorDetail(error)?.message === "string") return getErrorDetail(error).message;
  if (error?.response?.data?.error) return error.response.data.error;
  if (typeof error?.message === "string") return error.message;
  return fallback;
};

const formatChartDate = (isoDate) => {
  try {
    return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return isoDate;
  }
};

const percent = (value, total) => {
  if (!total) return 0;
  return Math.min(100, Math.round((value / total) * 100));
};

const formatAiProvider = (provider) => {
  if (provider === "xai") return "Grok";
  if (provider === "qwen") return "Qwen";
  return "Gemini";
};

const CATEGORY_LIBRARY = {
  behavioral: "Situational and collaboration stories that reveal how you work with other people.",
  technical: "Core role-specific knowledge, tooling, and implementation depth for the position.",
  "system design": "Architecture, tradeoffs, scalability, and high-level solution design questions.",
  leadership: "Ownership, influence, mentoring, and decision-making in ambiguous situations.",
  execution: "Planning, prioritization, delivery, and keeping projects moving under constraints.",
  communication: "Explaining complex ideas clearly to teammates, stakeholders, and interviewers.",
  "product thinking": "Understanding customers, product tradeoffs, and outcome-driven decisions.",
  "data & analytics": "Metrics, experimentation, analytics workflows, and data-informed reasoning.",
  "cloud & infrastructure": "Infrastructure, deployment, reliability, and cloud-platform experience.",
  security: "Risk management, privacy, secure design, and defensive engineering choices.",
  "role fit": "Questions that test motivation, company fit, and alignment with the specific role.",
};

const createCategoryOption = (name, selected = true) => ({
  id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  name,
  description:
    CATEGORY_LIBRARY[name.toLowerCase()] ||
    "Questions tailored to the responsibilities and expectations in this job posting.",
  selected,
});

const getSuggestedCategories = (draftJob) => {
  const draftCategories = Array.isArray(draftJob?.suggested_categories) ? draftJob.suggested_categories : [];
  if (draftCategories.length) {
    return draftCategories.slice(0, 5).map((name) => createCategoryOption(name, true));
  }

  const description = (draftJob?.description || "").toLowerCase();
  const heuristicMatches = [
    ["Behavioral", /stakeholder|team|collaborat|conflict|partner/],
    ["Technical", /python|java|javascript|react|sql|api|backend|frontend/],
    ["System Design", /system design|architecture|scalab|distributed|microservice/],
    ["Data & Analytics", /analytics|metric|sql|warehouse|experiment|data/],
    ["Leadership", /mentor|leadership|manage|ownership|influence/],
    ["Execution", /deliver|deadline|priorit|execution|roadmap|operate/],
    ["Communication", /communicat|present|written|verbal|explain/],
  ]
    .filter(([, regex]) => regex.test(description))
    .map(([name]) => createCategoryOption(name, true));

  return (heuristicMatches.length
    ? heuristicMatches
    : ["Behavioral", "Technical", "Execution", "Communication", "Role Fit"].map((name) =>
        createCategoryOption(name, true),
      )
  ).slice(0, 5);
};

const SUCCESS_STORIES = [
  {
    company: "Google",
    role: "Software Engineer",
    comp: "$185k base · $25k sign-on · equity",
    caption: "Cracked $185k/yr job at Google with Mockly",
    rotation: "rotate-[-1.6deg]",
    paper: "bg-[#f6f4ee]",
    topBars: ["w-4/5", "w-1/3"],
    meta: [
      [
        { kind: "text", value: "Candidate:" },
        { kind: "text", value: "H**** A****" },
      ],
      [
        { kind: "text", value: "Position:" },
        { kind: "text", value: "Software Engineer" },
      ],
      [
        { kind: "text", value: "Location:" },
        { kind: "text", value: "San Francisco, CA" },
      ],
    ],
    body: [
      [{ kind: "text", value: "We are pleased to extend" }, { kind: "redact", width: "w-20" }, { kind: "text", value: "an offer of employment." }],
      [{ kind: "text", value: "Your total target compensation includes" }, { kind: "text", value: "$185,000" }, { kind: "text", value: "in annual base salary." }],
      [{ kind: "text", value: "You will also be eligible for a" }, { kind: "text", value: "$25,000" }, { kind: "text", value: "signing bonus and" }, { kind: "redact", width: "w-16" }, { kind: "text", value: "equity award." }],
      [{ kind: "text", value: "This offer remains subject to company policies and standard background review." }],
    ],
  },
  {
    company: "Roblox",
    role: "Product Designer",
    comp: "$222k total target comp",
    caption: "Cracked $222k/yr job at Roblox with Mockly",
    rotation: "rotate-[1.4deg]",
    paper: "bg-[#f2f0eb]",
    topBars: ["w-2/3", "w-2/5", "w-1/4"],
    meta: [
      [{ kind: "text", value: "Offer of Employment" }],
      [{ kind: "text", value: "Candidate:" }, { kind: "redact", width: "w-16" }],
      [{ kind: "text", value: "Role:" }, { kind: "text", value: "Product Designer" }, { kind: "redact", width: "w-10" }],
    ],
    body: [
      [{ kind: "text", value: "We are excited to invite you to join Roblox in the position of" }, { kind: "text", value: "Product Designer" }, { kind: "text", value: "." }],
      [{ kind: "text", value: "Base Salary:" }, { kind: "text", value: "$178,000" }, { kind: "text", value: "· Signing Bonus:" }, { kind: "text", value: "$20,000" }],
      [{ kind: "text", value: "Equity Award:" }, { kind: "text", value: "Eligible" }, { kind: "text", value: "under the company stock plan." }],
      [{ kind: "text", value: "Please note that portions of this compensation package are" }, { kind: "redact", width: "w-24" }, { kind: "text", value: "and subject to committee approval." }],
    ],
  },
  {
    company: "NVIDIA",
    role: "ML Engineer",
    comp: "$196k base + bonus + RSUs",
    caption: "Cracked $196k/yr job at NVIDIA with Mockly",
    rotation: "rotate-[-0.8deg]",
    paper: "bg-[#f7f5ef]",
    topBars: ["w-3/4"],
    meta: [
      [{ kind: "text", value: "Private and Confidential" }],
      [{ kind: "text", value: "Position:" }, { kind: "text", value: "Machine Learning Engineer" }],
      [{ kind: "text", value: "Compensation Summary" }],
    ],
    body: [
      [{ kind: "text", value: "Base Salary:" }, { kind: "text", value: "$196,000" }],
      [{ kind: "text", value: "Annual Bonus:" }, { kind: "text", value: "Target bonus opportunity" }, { kind: "redact", width: "w-12" }],
      [{ kind: "text", value: "Equity Award:" }, { kind: "text", value: "RSUs vesting over time" }],
      [{ kind: "text", value: "Employment is contingent upon successful completion of" }, { kind: "redact", width: "w-20" }, { kind: "text", value: "and other onboarding requirements." }],
      [{ kind: "text", value: "We are thrilled about the possibility of you joining our team in" }, { kind: "text", value: "Santa Clara, CA" }, { kind: "text", value: "." }],
    ],
  },
  {
    company: "Snowflake",
    role: "Senior Data Engineer",
    comp: "$214k total target comp",
    caption: "Cracked $214k/yr job at Snowflake with Mockly",
    rotation: "rotate-[1deg]",
    paper: "bg-[#f3f1ec]",
    topBars: ["w-1/2", "w-3/5"],
    meta: [
      [{ kind: "text", value: "We are pleased to extend this offer" }],
      [{ kind: "text", value: "Candidate:" }, { kind: "text", value: "J**** R****" }],
      [{ kind: "text", value: "Department:" }, { kind: "text", value: "Data Platform Engineering" }],
    ],
    body: [
      [{ kind: "text", value: "Position:" }, { kind: "text", value: "Senior Data Engineer" }, { kind: "text", value: "· Location:" }, { kind: "text", value: "Remote / Bay Area" }],
      [{ kind: "text", value: "Base Salary:" }, { kind: "text", value: "$186,000" }, { kind: "text", value: "· Bonus:" }, { kind: "text", value: "$18,000 target" }],
      [{ kind: "text", value: "Equity Award:" }, { kind: "text", value: "Subject to board-approved grant" }],
      [{ kind: "text", value: "This offer is subject to company policies, eligibility verification, and" }, { kind: "redact", width: "w-20" }, { kind: "text", value: "." }],
    ],
  },
];

const SHOWCASE_WINDOWS = [
  {
    label: "Landing",
    eyebrow: "Mac-style preview",
    title: "See the full interview workflow before you even sign up.",
    description: "Cycle through the landing, dashboard, practice, and score views to understand the product in seconds.",
    stats: ["Landing → Job import", "Auth + saved jobs", "Practice + AI grading"],
    accent: "from-emerald-500/20 to-transparent",
  },
  {
    label: "Dashboard",
    eyebrow: "Saved progress",
    title: "Every job, question set, and score stays attached to the account.",
    description: "No more local-only mystery state. The dashboard reflects what was generated and what has already been practiced.",
    stats: ["3 active jobs", "24 questions saved", "7.9 average score"],
    accent: "from-cyan-500/20 to-transparent",
  },
  {
    label: "Practice",
    eyebrow: "Question flow",
    title: "Practice in voice or text mode with clear grading controls.",
    description: "Mockly keeps the UI minimal while still exposing depth, role level, and the exact categories being tested.",
    stats: ["Voice or text", "Fullscreen mode", "Role-aware grading"],
    accent: "from-violet-500/20 to-transparent",
  },
  {
    label: "Results",
    eyebrow: "Feedback loop",
    title: "Real scoring, category-aware feedback, and a smooth next-question flow.",
    description: "Every answer feeds your account history so progress charts and dashboard scores stay in sync automatically.",
    stats: ["Score breakdown", "Filler-word tracking", "Next-question CTA"],
    accent: "from-amber-500/20 to-transparent",
  },
];

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

const useAuth = () => useContext(AuthContext);
const useTheme = () => useContext(ThemeContext);

const applyTheme = (theme) => {
  document.body.classList.toggle("light-theme", theme === "light");
  document.body.classList.toggle("dark-theme", theme !== "light");
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(localStorage.getItem(THEME_KEY) || "dark");

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return null;
    }

    try {
      const response = await apiClient.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      clearAppState();
      setToken("");
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const handleAuthSuccess = useCallback((responseData) => {
    localStorage.setItem(TOKEN_KEY, responseData.token);
    setToken(responseData.token);
    setUser(responseData.user);
  }, []);

  const register = useCallback(async (payload) => {
    const response = await apiClient.post("/auth/register", payload);
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to create account");
    }
    handleAuthSuccess(response.data);
    return response.data.user;
  }, [handleAuthSuccess]);

  const login = useCallback(async (payload) => {
    const response = await apiClient.post("/auth/login", payload);
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to sign in");
    }
    handleAuthSuccess(response.data);
    return response.data.user;
  }, [handleAuthSuccess]);

  const logout = useCallback(async () => {
    try {
      if (localStorage.getItem(TOKEN_KEY)) {
        await apiClient.post("/auth/logout");
      }
    } catch {
      // Ignore logout network issues; the client still clears local state.
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      clearAppState();
      setToken("");
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ token, user, loading, refreshUser, register, login, logout, isAuthenticated: Boolean(user) }),
    [token, user, loading, refreshUser, register, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const LoadingScreen = ({ label = "Loading..." }) => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-4" />
      <p className="text-gray-400">{label}</p>
    </div>
  </div>
);

const RequireAuth = ({ children }) => {
  const auth = useAuth();
  if (auth.loading) return <LoadingScreen label="Checking your account..." />;
  if (!auth.user) return <Navigate to="/login" replace />;
  return children;
};

const LandingNavbar = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleStart = () => {
    navigate(auth.user ? "/app/dashboard" : "/signup");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-8 py-5 flex items-center justify-between bg-[#050505]/90 backdrop-blur-md">
      <Link to="/" className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-400 rounded-lg flex items-center justify-center">
          <FileText className="w-4 h-4 text-white" />
        </div>
        <span className="text-xl font-medium text-white tracking-tight">Mockly</span>
      </Link>
      <div className="hidden md:flex items-center gap-8">
        <a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm">Features</a>
        <Link to="/packages" className="text-gray-400 hover:text-white transition-colors text-sm">Company Packs</Link>
        <Link to="/feedback" className="text-gray-400 hover:text-white transition-colors text-sm">Feedback</Link>
      </div>
      <div className="flex items-center gap-3">
        {auth.user ? (
          <button onClick={() => navigate("/app/dashboard")} className="border border-white/10 text-white rounded-full px-5 py-2 text-sm hover:bg-white/5 transition-colors">
            Dashboard
          </button>
        ) : (
          <button onClick={() => navigate("/login")} className="border border-white/10 text-white rounded-full px-5 py-2 text-sm hover:bg-white/5 transition-colors">
            Sign in
          </button>
        )}
        <button onClick={handleStart} className="bg-emerald-500 text-black font-medium rounded-full px-5 py-2 text-sm hover:bg-emerald-400 transition-colors">
          Start free
        </button>
      </div>
    </nav>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  return (
    <section className="pt-36 pb-24 px-8">
      <div className="max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-8">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-gray-400 text-sm">AI-powered interview preparation</span>
        </div>
        <h1 className="hero-title text-5xl md:text-7xl lg:text-8xl text-white mb-6 leading-[1.1] tracking-tight">
          Nail your next<br /><span className="text-gradient">interview</span>
        </h1>
        <p className="text-gray-400 text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
          Paste a job posting, practice out loud, and get graded — with full control over every step.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={() => navigate(auth.user ? "/app/dashboard" : "/signup")} className="group bg-emerald-500 text-black font-medium rounded-full px-8 py-4 text-base hover:bg-emerald-400 transition-all flex items-center gap-2">
            Start practicing free
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => navigate("/packages")} className="text-gray-400 hover:text-white transition-colors px-8 py-4 text-base flex items-center gap-2">
            <Package className="w-4 h-4" /> View company packs
          </button>
        </div>
      </div>
    </section>
  );
};

const ShowcaseSection = () => {
  const [activeWindow, setActiveWindow] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveWindow((current) => (current + 1) % SHOWCASE_WINDOWS.length);
    }, 3500);
    return () => window.clearInterval(intervalId);
  }, []);

  const current = SHOWCASE_WINDOWS[activeWindow];

  return (
    <section className="px-8 pb-24">
      <div className="max-w-6xl mx-auto">
        <div className="browser-mockup rounded-[28px] border border-white/10 bg-[#0c0c0c] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            </div>
            <div className="hidden md:flex items-center gap-2">
              {SHOWCASE_WINDOWS.map((windowItem, index) => (
                <button
                  key={windowItem.label}
                  onClick={() => setActiveWindow(index)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    activeWindow === index
                      ? "border-white/20 bg-white/10 text-white"
                      : "border-white/10 text-gray-500 hover:text-white"
                  }`}
                >
                  {windowItem.label}
                </button>
              ))}
            </div>
            <div className="text-gray-500 text-xs">mockly.app</div>
          </div>

          <div className={`grid lg:grid-cols-[1.1fr_0.9fr] bg-gradient-to-br ${current.accent}`}>
            <div className="p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-white/10">
              <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-4">{current.eyebrow}</p>
              <h3 className="text-2xl md:text-3xl text-white font-serif mb-4 max-w-xl">{current.title}</h3>
              <p className="text-gray-400 leading-relaxed mb-8 max-w-xl">{current.description}</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {current.stats.map((stat) => (
                  <div key={stat} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4 text-sm text-gray-300">
                    {stat}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="rounded-[24px] border border-white/10 bg-[#050505]/90 p-4 min-h-[360px]">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-medium">{current.label} view</p>
                      <p className="text-gray-500 text-sm">Interactive product preview</p>
                    </div>
                    <div className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1 text-xs">
                      Live concept
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[72, 45, 58].map((value, index) => (
                      <div key={value} className="rounded-xl bg-black/30 border border-white/10 p-3">
                        <p className="text-gray-500 text-xs mb-2">Panel {index + 1}</p>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-[0.82fr_1.18fr] gap-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-3">Sidebar</p>
                    {["Dashboard", "Progress", "Questions", "Feedback"].map((item, index) => (
                      <div
                        key={item}
                        className={`rounded-xl px-3 py-2 text-sm mb-2 ${
                          index === activeWindow ? "bg-white/10 text-white" : "text-gray-500 bg-black/20"
                        }`}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-white font-medium">Mock interview session</p>
                        <p className="text-gray-500 text-sm">Everything styled like the real app</p>
                      </div>
                      <div className="text-gray-500 text-xs">Step {activeWindow + 1}/4</div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                        <p className="text-gray-500 text-xs mb-2">Question</p>
                        <p className="text-white text-sm">How would you prioritize ambiguous work while keeping stakeholders aligned?</p>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                        <p className="text-gray-500 text-xs mb-2">AI insight</p>
                        <p className="text-gray-300 text-sm">Categories, progress, and the next best action stay visible instead of leaving users guessing.</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {["Behavioral", "Execution", "Communication"].map((tag, index) => (
                          <span
                            key={tag}
                            className={`text-xs px-2.5 py-1 rounded-full ${
                              ["bg-emerald-500/15 text-emerald-300", "bg-violet-500/15 text-violet-300", "bg-cyan-500/15 text-cyan-300"][index]
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const OfferArtifactLine = ({ segments, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] leading-relaxed text-black/75 ${className}`}>
    {segments.map((segment, index) =>
      segment.kind === "redact" ? (
        <span key={`${segment.width}-${index}`} className={`inline-block h-[0.72em] ${segment.width} rounded-[2px] bg-black align-middle`} />
      ) : (
        <span key={`${segment.value}-${index}`} className={segment.emphasis ? "font-semibold text-black/85" : ""}>{segment.value}</span>
      ),
    )}
  </div>
);

const OfferArtifactCard = ({ artifact }) => (
  <div className="group">
    <div className={`relative overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.28)] transition-all duration-300 group-hover:-translate-y-1.5 group-hover:scale-[1.02] group-hover:shadow-[0_28px_80px_rgba(0,0,0,0.38)] ${artifact.rotation}`}>
      <div className={`relative min-h-[420px] ${artifact.paper} px-5 py-6 md:px-6`}>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.5),transparent_28%)] pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[52px] md:text-[66px] font-semibold tracking-[0.08em] text-black/[0.1] rotate-[-25deg] select-none">MOCKLY VERIFIED</span>
        </div>

        <div className="relative z-10">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-xl font-semibold text-black tracking-tight">{artifact.company}</p>
              <p className="text-[11px] uppercase tracking-[0.28em] text-black/45">Offer of Employment</p>
            </div>
            <div className="text-right text-[11px] uppercase tracking-[0.22em] text-black/35">
              Confidential
            </div>
          </div>

          <div className="space-y-2.5 mb-5">
            {artifact.topBars.map((width) => (
              <div key={width} className={`h-[7px] rounded-[2px] bg-black ${width}`} />
            ))}
          </div>

          <div className="space-y-2 mb-5">
            {artifact.meta.map((segments, index) => (
              <OfferArtifactLine key={index} segments={segments} className={index === 0 ? "font-medium text-black/80" : ""} />
            ))}
          </div>

          <div className="border-y border-black/10 py-4 space-y-2.5 mb-5">
            {artifact.body.map((segments, index) => (
              <OfferArtifactLine key={index} segments={segments} />
            ))}
          </div>

          <div className="rounded-2xl border border-black/10 bg-black/[0.03] p-4 mb-4">
            <p className="text-[11px] uppercase tracking-[0.24em] text-black/40 mb-2">Compensation snapshot</p>
            <p className="text-sm text-black/75">{artifact.role}</p>
            <p className="text-sm font-medium text-black/85 mt-1">{artifact.comp}</p>
          </div>

          <div className="flex items-center justify-between text-[11px] text-black/40 uppercase tracking-[0.18em]">
            <span>Partially redacted for privacy</span>
            <span>View offer →</span>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-4 px-1">
      <p className="text-white text-sm font-medium">{artifact.caption}</p>
      <p className="text-gray-500 text-xs mt-1">Anonymized and reformatted for the Mockly proof wall.</p>
    </div>
  </div>
);

const SuccessStoriesSection = () => (
  <section className="px-8 pb-24">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <p className="text-emerald-400 text-sm tracking-widest uppercase mb-4">Proof</p>
        <h2 className="section-title text-4xl md:text-5xl text-white mb-4">Real-looking offer proof, anonymized.</h2>
        <p className="text-gray-400 max-w-3xl mx-auto text-lg">
          Built to feel like actual submitted offer letters — partially readable, partially redacted, slightly messy, and premium.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
        {SUCCESS_STORIES.map((artifact) => (
          <OfferArtifactCard key={artifact.company} artifact={artifact} />
        ))}
      </div>
    </div>
  </section>
);

const FeaturesSection = () => {
  const features = [
    { icon: Upload, title: "One-click URL scrape", description: "Paste a job link. We scrape and parse it automatically." },
    { icon: FileText, title: "Resume cross-reference", description: "Upload your CV. Questions personalized to your background." },
    { icon: Package, title: "Company packs", description: "Amazon, Google, Meta, Anthropic & more with real questions.", badge: "PRO" },
    { icon: Sparkles, title: "AI-powered generation", description: "Smart questions tailored to job requirements." },
    { icon: Mic, title: "Voice practice", description: "Practice out loud with real-time transcription." },
    { icon: Check, title: "STAR & filler analysis", description: "Get feedback on structure, filler words, and impact." },
  ];
  return (
    <section id="features" className="py-32 px-8 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-emerald-400 text-sm tracking-widest uppercase mb-4">Features</p>
          <h2 className="section-title text-4xl md:text-5xl text-white mb-4">Everything you need.</h2>
          <h2 className="section-title text-4xl md:text-5xl text-gray-500 italic">Nothing you don't.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div key={feature.title} className="group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all">
              <feature.icon className="w-8 h-8 text-gray-500 mb-4 group-hover:text-emerald-400 transition-colors" strokeWidth={1.5} />
              <h3 className="text-white text-lg font-medium mb-2 flex items-center gap-2">
                {feature.title}
                {feature.badge && <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">{feature.badge}</span>}
              </h3>
              <p className="text-gray-500 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="px-8 py-12 border-t border-white/5">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <span className="text-white font-serif text-lg">Mockly</span>
      <p className="text-gray-500 text-sm">© 2026 Mockly. Built for job seekers.</p>
      <div className="flex items-center gap-6">
        <Link to="/packages" className="text-gray-500 hover:text-white text-sm transition-colors">Company Packs</Link>
        <Link to="/feedback" className="text-gray-500 hover:text-white text-sm transition-colors">Feedback</Link>
      </div>
    </div>
  </footer>
);

const LandingPage = () => (
  <div className="min-h-screen bg-[#050505]">
    <LandingNavbar />
    <HeroSection />
    <ShowcaseSection />
    <SuccessStoriesSection />
    <FeaturesSection />
    <Footer />
  </div>
);

const AuthPage = ({ mode }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!auth.loading && auth.user) {
      navigate("/app/dashboard", { replace: true });
    }
  }, [auth.loading, auth.user, navigate]);

  const isSignup = mode === "signup";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      if (isSignup) {
        await auth.register({ name, email, password });
      } else {
        await auth.login({ email, password });
      }
      navigate("/app/dashboard", { replace: true });
    } catch (submitError) {
      setError(getErrorMessage(submitError, isSignup ? "Could not create account" : "Could not sign in"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (auth.loading) return <LoadingScreen label="Preparing your account..." />;

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-10 flex items-center justify-center">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-10 items-center">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-400 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-medium text-white tracking-tight">Mockly</span>
          </Link>
          <p className="text-emerald-400 text-sm tracking-widest uppercase mb-4">Account</p>
          <h1 className="text-5xl md:text-6xl font-serif text-white mb-6">
            {isSignup ? "Create your account." : "Welcome back."}
          </h1>
          <p className="text-gray-400 text-lg max-w-xl leading-relaxed">
            {isSignup
              ? "Start tracking interview practice with real account-based history, saved jobs, and AI feedback that follows you across devices."
              : "Pick up exactly where you left off with your saved jobs, question sets, and interview feedback history."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">{isSignup ? "New account" : "Sign in"}</p>
              <p className="text-gray-500 text-sm">{isSignup ? "Real account-based data starts here." : "Your saved progress is waiting."}</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {isSignup && (
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Name</label>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                placeholder="Your name"
                className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/20"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-400 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              placeholder="you@example.com"
              className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/20"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-400 text-sm mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/20"
            />
          </div>

          <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2 disabled:opacity-60">
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : isSignup ? "Create account" : "Sign in"}
          </button>

          <div className="mt-6 text-sm text-gray-500 text-center">
            {isSignup ? (
              <>
                Already have an account? <Link to="/login" className="text-white hover:text-emerald-400">Sign in</Link>
              </>
            ) : (
              <>
                Need an account? <Link to="/signup" className="text-white hover:text-emerald-400">Create one</Link>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const LoginPage = () => <AuthPage mode="login" />;
const SignupPage = () => <AuthPage mode="signup" />;

const AppNavbar = ({ activeTab }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const theme = useTheme();
  const isSubscribed = Boolean(auth.user?.is_subscribed);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#050505]/95 backdrop-blur-md border-b border-white/5">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/app/dashboard" className="text-xl font-medium text-white tracking-tight">Mockly</Link>
          <div className="hidden md:flex items-center gap-2 bg-white/5 rounded-lg p-1">
            {['dashboard', 'progress', 'history', 'feedback'].map((tab) => (
              <Link
                key={tab}
                to={tab === 'feedback' ? '/feedback' : `/app/${tab}`}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === tab ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`hidden md:inline-flex text-xs px-3 py-1.5 rounded-full border ${
            isSubscribed
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
              : "bg-white/5 text-gray-400 border-white/10"
          }`}>
            {isSubscribed ? "Pro Plan" : "Free Plan"}
          </span>
          <button onClick={() => navigate('/app/billing')} className="hidden md:flex bg-emerald-500/10 text-emerald-400 text-sm px-3 py-1.5 rounded-full border border-emerald-500/20 items-center gap-1">
            {isSubscribed ? <CreditCard className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />} {isSubscribed ? "Billing" : "Upgrade"}
          </button>
          <button onClick={() => navigate('/app/account')} className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-300 text-sm hover:bg-white/10">
            <User className="w-4 h-4 text-gray-500" />
            <span>{auth.user?.name || auth.user?.email}</span>
          </button>
          <button onClick={theme.toggleTheme} title={theme.theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} className="p-2 text-gray-500 hover:text-white">
            {theme.theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>h
          <button onClick={async () => { navigate('/', { replace: true }); await auth.logout(); }} className="border border-white/10 text-white px-4 py-2 rounded-full text-sm hover:bg-white/5 flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </nav>
  );
};

const PackagesPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const packages = getAllPackages();
  const [creatingPackageId, setCreatingPackageId] = useState("");
  const [error, setError] = useState("");
  const isSubscribed = Boolean(auth.user?.is_subscribed);

  const handleSelectPackage = async (pkg) => {
    if (!isSubscribed) {
      navigate('/app/billing');
      return;
    }

    setCreatingPackageId(pkg.id);
    setError("");
    try {
      const response = await apiClient.post("/jobs", {
        job_title: "Interview Prep",
        company: pkg.name,
        seniority: "Mixed",
        description: pkg.description,
        categories: pkg.categories,
        questions: pkg.questions.map((question) => ({
          text: question.text,
          categories: question.categories,
          source: question.source,
          difficulty: question.difficulty,
        })),
        is_package: true,
        package_id: pkg.id,
      });
      if (!response.data.success || !response.data.job) {
        throw new Error(response.data.error || "Could not load package");
      }
      saveCurrentJobId(response.data.job.id);
      saveCurrentQuestionId(response.data.job.questions?.[0]?.id || "");
      navigate('/app/questions');
    } catch (loadError) {
      if (getErrorDetail(loadError)?.code === 'subscription_required') {
        navigate('/app/billing');
      } else {
        setError(getErrorMessage(loadError, "Failed to load company pack"));
      }
    } finally {
      setCreatingPackageId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <LandingNavbar />
      <main className="pt-28 px-6 pb-12 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-gray-400 text-sm">Pro-only company packs</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-white mb-4">Company Interview Packs</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Real questions from real interviews. Sourced from Glassdoor, Blind, Reddit, and more.
          </p>
        </div>

        {!isSubscribed && (
          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-3xl p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-2">Upgrade required</p>
              <h2 className="text-white text-2xl font-serif mb-2">Unlock FAANG-style question banks.</h2>
              <p className="text-gray-400 max-w-2xl">Free users can still generate job-specific question sets, but curated company packs are reserved for Pro.</p>
            </div>
            <button onClick={() => navigate('/app/billing')} className="px-5 py-3 rounded-xl bg-emerald-500 text-black font-medium hover:bg-emerald-400">
              Upgrade to Pro
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => {
            const locked = !isSubscribed;
            return (
              <div
                key={pkg.id}
                onClick={() => handleSelectPackage(pkg)}
                className={`relative bg-[#0c0c0c] border rounded-2xl p-6 transition-all group ${
                  locked
                    ? 'border-white/10 cursor-pointer hover:border-emerald-500/20'
                    : 'border-white/10 hover:border-white/20 cursor-pointer'
                }`}
              >
                {locked && (
                  <div className="absolute inset-0 rounded-2xl bg-black/35 backdrop-blur-[1px] border border-emerald-500/10 pointer-events-none" />
                )}
                <div className={`relative ${locked ? 'blur-[1.5px]' : ''}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl" style={{ backgroundColor: `${pkg.color}20` }}>
                      {pkg.logo}
                    </div>
                    <div>
                      <h3 className="text-white text-xl font-medium">{pkg.name}</h3>
                      <p className="text-gray-500 text-sm">{pkg.questionCount} questions</p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">{pkg.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {pkg.categories.slice(0, 3).map((cat) => (
                      <span key={cat} className="bg-white/5 text-gray-400 text-xs px-2 py-1 rounded">{cat}</span>
                    ))}
                  </div>
                </div>

                <div className="relative flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded ${
                    pkg.difficulty === 'Very Hard' ? 'bg-red-500/20 text-red-400' :
                    pkg.difficulty === 'Hard' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{pkg.difficulty}</span>
                  <span className="text-emerald-400 text-sm group-hover:underline flex items-center gap-2">
                    {locked ? <><Lock className="w-4 h-4" /> Unlock with Pro</> : (creatingPackageId === pkg.id ? 'Loading…' : 'Start practicing →')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};


const FeedbackPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-[#050505]">
        <Link to="/" className="text-xl font-serif italic text-white">Mockly</Link>
        <button onClick={() => navigate('/app/dashboard')} className="border border-white/20 text-white px-4 py-2 rounded-full text-sm hover:bg-white/5">Dashboard</button>
      </nav>
      <main className="pt-24 px-6 pb-12 max-w-2xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-serif text-white mb-8">Feedback</h1>
        <p className="text-gray-300 text-xl mb-8">Mockly is built for engineers who actually want to get better.</p>
        <p className="text-gray-400 text-lg mb-4"> If something feels off, confusing, or missing — I want to hear it.Your feedback directly shapes the product.</p>

        <p className="text-gray-400 text-lg mb-12"> I read every message.</p>
        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6">
          <a href="https://t.me/squirrel045" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-emerald-400 transition-colors mb-4">
            <Send className="w-5 h-5" />
            <span className="text-lg">@squirrel045</span>
          </a>
          <p className="text-gray-500 text-sm">Bug reports, feature requests, general thoughts — anything goes. I&apos;ll get back to you.</p>
        </div>
      </main>
    </div>
  );
};

const BillingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { refreshUser } = auth;
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ctaLoading, setCtaLoading] = useState("");
  const [error, setError] = useState("");

  const loadBilling = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/billing/status");
      setBilling(response.data);
      await refreshUser();
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Failed to load billing"));
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    if (!location.search.includes("checkout=")) return;
    loadBilling();
    if (!location.search.includes("checkout=success")) return undefined;
    const intervalId = window.setInterval(() => {
      loadBilling();
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [loadBilling, location.search]);

  const redirectToStripe = async (endpoint, stateKey) => {
    setCtaLoading(stateKey);
    setError("");
    try {
      const response = await apiClient.post(endpoint);
      if (!response.data?.url) {
        throw new Error("Missing Stripe redirect URL");
      }
      window.location.assign(response.data.url);
    } catch (checkoutError) {
      setError(getErrorMessage(checkoutError, "Unable to open Stripe"));
      setCtaLoading("");
    }
  };

  if (loading) return <LoadingScreen label="Loading billing..." />;

  const checkoutState = new URLSearchParams(location.search).get("checkout");
  const isSubscribed = Boolean(billing?.is_subscribed);
  const freeRemaining = billing?.free_assessments_remaining ?? Math.max((billing?.free_assessment_limit || 0) - (billing?.free_assessments_used || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-serif text-white mb-2">Billing</h1>
            <p className="text-gray-500">Unlock unlimited result assessments and keep Stripe-managed billing in one place.</p>
          </div>
          <button onClick={() => navigate('/app/dashboard')} className="border border-white/10 text-white px-4 py-2 rounded-full text-sm hover:bg-white/5">
            Back to dashboard
          </button>
        </div>

        {checkoutState === "success" && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 mb-6 text-emerald-300">
            Stripe checkout completed. Refreshing your subscription status now.
          </div>
        )}
        {checkoutState === "cancel" && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 mb-6 text-yellow-300">
            Checkout was canceled. Your free plan is still active.
          </div>
        )}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-7">
            <p className="text-gray-500 text-sm uppercase tracking-[0.2em] mb-3">Current access</p>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isSubscribed ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white"}`}>
                {isSubscribed ? <Crown className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
              </div>
              <div>
                <p className="text-white text-xl font-medium">{isSubscribed ? "Mockly Pro" : "Mockly Free"}</p>
                <p className="text-gray-500 text-sm">{billing?.subscription_status || "No active subscription"}</p>
              </div>
            </div>
            <div className="rounded-2xl bg-black/20 border border-white/10 p-4 mb-4">
              <p className="text-gray-500 text-sm mb-2">Free assessments used</p>
              <p className="text-white text-3xl font-serif">{billing?.free_assessments_used || 0}</p>
              <p className="text-gray-500 text-sm mt-2">
                {isSubscribed ? "Unlimited result assessments unlocked." : `${freeRemaining} of ${billing?.free_assessment_limit || 2} free result assessments remaining.`}
              </p>
            </div>
            {billing?.subscription_current_period_end && (
              <p className="text-gray-500 text-sm">
                Current billing period ends {new Date(billing.subscription_current_period_end).toLocaleDateString()}.
              </p>
            )}
          </div>

          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-3xl p-7 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-1 bg-emerald-500" />
            <p className="text-emerald-400 text-sm uppercase tracking-[0.2em] mb-3">Recommended</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white text-xl font-medium">Mockly Pro</p>
                <p className="text-gray-500 text-sm">Backed by Stripe Checkout + customer portal</p>
              </div>
            </div>
            <p className="text-white text-4xl font-serif mb-1">$19<span className="text-lg text-gray-500">/month</span></p>
            <p className="text-gray-400 mb-6">Unlimited result assessments, account-based billing, and a clean upgrade path once free scans run out.</p>
            <div className="space-y-3 mb-6">
              {[
                "Unlimited AI result assessments",
                "Stripe-hosted checkout with promo-code support",
                "Manage billing and cancellations from the portal",
                "Keeps the same existing UI and account flow",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-emerald-400" /> {item}
                </div>
              ))}
            </div>
            {isSubscribed ? (
              <button
                onClick={() => redirectToStripe("/billing/portal", "portal")}
                disabled={!billing?.portal_ready || ctaLoading === "portal"}
                className="w-full bg-white text-black font-medium py-4 rounded-xl hover:bg-gray-200 disabled:opacity-60"
              >
                {ctaLoading === "portal" ? "Opening portal..." : "Manage subscription"}
              </button>
            ) : (
              <button
                onClick={() => redirectToStripe("/billing/checkout", "checkout")}
                disabled={!billing?.checkout_ready || ctaLoading === "checkout"}
                className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 disabled:opacity-60"
              >
                {ctaLoading === "checkout" ? "Opening Stripe..." : "Upgrade with Stripe"}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const AccountPage = () => {
  const auth = useAuth();
  const [name, setName] = useState(auth.user?.name || "");
  const [email, setEmail] = useState(auth.user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [error, setError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    setName(auth.user?.name || "");
    setEmail(auth.user?.email || "");
  }, [auth.user?.name, auth.user?.email]);

  const handleProfileSave = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setError("");
    setProfileMessage("");
    try {
      const response = await apiClient.patch("/auth/profile", { name, email });
      if (!response.data.success) {
        throw new Error(response.data.error || "Could not update profile");
      }
      localStorage.setItem(TOKEN_KEY, response.data.token);
      await refreshUser();
      setProfileMessage("Profile updated.");
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Could not update profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (event) => {
    event.preventDefault();
    setSavingPassword(true);
    setError("");
    setPasswordMessage("");
    try {
      const response = await apiClient.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (!response.data.success) {
        throw new Error(response.data.error || "Could not change password");
      }
      localStorage.setItem(TOKEN_KEY, response.data.token);
      setCurrentPassword("");
      setNewPassword("");
      setPasswordMessage("Password changed.");
      await refreshUser();
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Could not change password"));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-serif text-white mb-2">Account settings</h1>
          <p className="text-gray-500">Manage your profile, password, and the subscription that follows your account across devices.</p>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 text-red-300">{error}</div>}

        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={handleProfileSave} className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">Profile</p>
                <p className="text-gray-500 text-sm">Basic account information used throughout the app.</p>
              </div>
            </div>

            <label className="block text-gray-400 text-sm mb-2">Name</label>
            <input value={name} onChange={(event) => setName(event.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white mb-4" />

            <label className="block text-gray-400 text-sm mb-2">Email</label>
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white mb-6" />

            {profileMessage && <p className="text-emerald-400 text-sm mb-4">{profileMessage}</p>}

            <button type="submit" disabled={savingProfile} className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 disabled:opacity-60 flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> {savingProfile ? "Saving..." : "Save profile"}
            </button>
          </form>

          <form onSubmit={handlePasswordSave} className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">Password</p>
                <p className="text-gray-500 text-sm">Change your password before deployment so the account is production-ready.</p>
              </div>
            </div>

            <label className="block text-gray-400 text-sm mb-2">Current password</label>
            <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white mb-4" />

            <label className="block text-gray-400 text-sm mb-2">New password</label>
            <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white mb-6" minLength={8} />

            {passwordMessage && <p className="text-emerald-400 text-sm mb-4">{passwordMessage}</p>}

            <button type="submit" disabled={savingPassword} className="w-full bg-white text-black font-medium py-4 rounded-xl hover:bg-gray-200 disabled:opacity-60">
              {savingPassword ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

const HistoryPage = () => {
  const [mode, setMode] = useState("interview");
  const [interviewAttempts, setInterviewAttempts] = useState([]);
  const [codingAttempts, setCodingAttempts] = useState([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadAttempts = async () => {
      setLoading(true);
      setError("");
      try {
        const [interviewResponse, codingResponse] = await Promise.all([
          apiClient.get("/attempts"),
          apiClient.get("/coding/attempts"),
        ]);
        const interviewRows = interviewResponse.data.attempts || [];
        const codingRows = codingResponse.data.attempts || [];
        setInterviewAttempts(interviewRows);
        setCodingAttempts(codingRows);
        const firstId = interviewRows[0]?.id || codingRows[0]?.id || "";
        setSelectedAttemptId(firstId);
        if (!interviewRows.length && codingRows.length) {
          setMode("coding");
        }
      } catch (loadError) {
        setError(getErrorMessage(loadError, "Failed to load attempt history"));
      } finally {
        setLoading(false);
      }
    };
    loadAttempts();
  }, []);

  useEffect(() => {
    const rows = mode === "coding" ? codingAttempts : interviewAttempts;
    if (!rows.find((attempt) => attempt.id === selectedAttemptId)) {
      setSelectedAttemptId(rows[0]?.id || "");
    }
  }, [mode, interviewAttempts, codingAttempts, selectedAttemptId]);

  if (loading) return <LoadingScreen label="Loading attempt history..." />;

  const attempts = mode === "coding" ? codingAttempts : interviewAttempts;
  const selectedAttempt = attempts.find((attempt) => attempt.id === selectedAttemptId) || attempts[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="history" />
      <main className="pt-24 px-6 pb-12 max-w-6xl mx-auto">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-serif text-white mb-2">Attempt history</h1>
            <p className="text-gray-500">Review previous interview answers and coding submissions in one place.</p>
          </div>
          <div className="flex bg-white/5 rounded-lg p-1">
            {[
              ["interview", `Interview (${interviewAttempts.length})`],
              ["coding", `Coding (${codingAttempts.length})`],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  mode === value ? "bg-white/10 text-white" : "text-gray-500 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 text-red-300">{error}</div>}

        {attempts.length === 0 ? (
          <div className="bg-[#0c0c0c] border border-dashed border-white/10 rounded-2xl p-12 text-center">
            <History className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">No {mode} assessments yet.</p>
            <p className="text-gray-500 text-sm">
              {mode === "coding"
                ? "Generate an adaptive coding round and your submissions will show up here."
                : "Once you practice and score answers, they’ll appear here with full feedback history."}
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6">
            <div className="space-y-3">
              {attempts.map((attempt) => (
                <button
                  key={attempt.id}
                  onClick={() => setSelectedAttemptId(attempt.id)}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    selectedAttempt?.id === attempt.id ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-[#0c0c0c]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-white font-medium">{attempt.job_title}</p>
                      <p className="text-gray-500 text-sm">{attempt.company}</p>
                    </div>
                    <span className="text-emerald-400 text-sm">{attempt.overall_score?.toFixed(1)}/10</span>
                  </div>
                  <p className="text-gray-300 text-sm mb-3 max-h-12 overflow-hidden">
                    {mode === "coding" ? attempt.title : attempt.question}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 gap-2">
                    <span>{new Date(attempt.created_at).toLocaleString()}</span>
                    <span>{formatAiProvider(attempt.provider)}</span>
                  </div>
                </button>
              ))}
            </div>

            {selectedAttempt && (
              <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-7">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white text-2xl font-serif">{selectedAttempt.job_title}</p>
                    <p className="text-gray-500 text-sm">{selectedAttempt.company}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 text-3xl font-serif">{selectedAttempt.overall_score?.toFixed(1)}</p>
                    <p className="text-gray-500 text-sm">overall score</p>
                  </div>
                </div>

                {mode === "coding" ? (
                  <>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Challenge</p>
                      <p className="text-white">{selectedAttempt.title}</p>
                      <p className="text-gray-500 text-sm mt-2">
                        {selectedAttempt.difficulty} · {selectedAttempt.language} · {selectedAttempt.passed}/{selectedAttempt.total} tests passed
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-5">
                      {[
                        ["Correctness", selectedAttempt.correctness_score],
                        ["Quality", selectedAttempt.code_quality_score],
                        ["Efficiency", selectedAttempt.efficiency_score],
                      ].map(([label, score]) => (
                        <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                          <p className="text-gray-500 text-sm mb-1">{label}</p>
                          <p className="text-white text-2xl font-serif">{Number(score || 0).toFixed(1)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Submitted code</p>
                      <pre className="rounded-2xl border border-white/10 bg-black/20 p-4 text-gray-300 whitespace-pre-wrap max-h-72 overflow-auto text-sm font-mono">{selectedAttempt.code}</pre>
                    </div>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Feedback</p>
                      <p className="text-gray-300 whitespace-pre-line">{selectedAttempt.feedback}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Question</p>
                      <p className="text-white">{selectedAttempt.question}</p>
                    </div>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Your answer</p>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-gray-300 whitespace-pre-line max-h-48 overflow-auto">
                        {selectedAttempt.answer}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-5">
                      {[
                        ["Structure", selectedAttempt.structure_score],
                        ["Impact", selectedAttempt.impact_score],
                        ["Clarity", selectedAttempt.clarity_score],
                        ["Role fit", selectedAttempt.role_fit_score],
                      ].map(([label, score]) => (
                        <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                          <p className="text-gray-500 text-sm mb-1">{label}</p>
                          <p className="text-white text-2xl font-serif">{Number(score || 0).toFixed(1)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mb-5">
                      <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Feedback</p>
                      <p className="text-gray-300 whitespace-pre-line">{selectedAttempt.feedback}</p>
                    </div>
                    {selectedAttempt.filler_word_count > 0 && (
                      <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-4">
                        <p className="text-yellow-300 font-medium mb-2">Filler words: {selectedAttempt.filler_word_count}</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(selectedAttempt.filler_words || {}).map(([word, count]) => (
                            <span key={word} className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 text-sm">
                              {word} × {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};


const DashboardPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const [jobsResponse, statsResponse] = await Promise.all([
        apiClient.get("/jobs"),
        apiClient.get("/stats"),
      ]);
      setJobs(jobsResponse.data.jobs || []);
      setStats(statsResponse.data);
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Failed to load dashboard"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleDeleteJob = async (jobId) => {
    try {
      await apiClient.delete(`/jobs/${jobId}`);
      if (getCurrentJobId() === jobId) {
        clearAppState();
      }
      await loadDashboard();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Could not delete job"));
    }
  };

  const resumeJob = jobs.find((job) => job.id === getCurrentJobId()) || null;
  const resumeQuestionId = resumeJob ? getCurrentQuestionId() : "";

  if (loading) return <LoadingScreen label="Loading your dashboard..." />;

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-serif text-white mb-2">Welcome back</h1>
        <p className="text-gray-500 mb-8">Here's an overview of your interview prep.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {resumeJob && (
          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-3xl p-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-2">Resume where you left off</p>
                <h2 className="text-white text-2xl font-serif mb-1">{resumeJob.job_title}</h2>
                <p className="text-gray-500 text-sm">{resumeJob.company} · {resumeJob.practiced_count}/{resumeJob.question_count} questions practiced</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => { saveCurrentJobId(resumeJob.id); navigate('/app/questions'); }} className="px-4 py-2 rounded-xl border border-white/10 text-white hover:bg-white/5">
                  Open workspace
                </button>
                <button onClick={() => { saveCurrentJobId(resumeJob.id); navigate(resumeQuestionId ? '/app/practice' : '/app/questions'); }} className="px-4 py-2 rounded-xl bg-emerald-500 text-black font-medium hover:bg-emerald-400">
                  Continue practice
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Job Postings</p>
              <Trash2 className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.jobs_count || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Interview Rounds</p>
              <FileText className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.interview_attempts || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Coding Rounds</p>
              <Code2 className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.coding_attempts || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Average Score</p>
              <Target className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{(stats?.avg_score || 0).toFixed(1)}</p>
          </div>
        </div>

        <details className="mb-8" open>
          <summary className="flex items-center gap-2 text-gray-400 cursor-pointer hover:text-white">
            <ChevronRight className="w-4 h-4" />
            <span className="text-sm">Usage & Billing</span>
          </summary>
          <div className="mt-3 grid md:grid-cols-2 gap-4">
            <div className="bg-[#0c0c0c] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">AI Generations</span>
                <span className="text-white text-sm">
                  {Math.max((stats?.daily_generations_limit || 0) - (stats?.daily_generations_used || 0), 0)} / {stats?.daily_generations_limit || 0} remaining
                </span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${percent(stats?.daily_generations_used || 0, stats?.daily_generations_limit || 1)}%` }} />
              </div>
            </div>
            <div className="bg-[#0c0c0c] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Free result assessments</span>
                <span className="text-white text-sm">
                  {stats?.is_subscribed ? "Unlimited" : `${stats?.free_assessments_remaining ?? 0} left`}
                </span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-emerald-500" style={{ width: `${percent(stats?.free_assessments_used || 0, stats?.free_assessment_limit || 1)}%` }} />
              </div>
              <button onClick={() => navigate('/app/billing')} className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> {stats?.is_subscribed ? "Manage Stripe billing" : "Upgrade after 2 free scans"}
              </button>
            </div>
          </div>
        </details>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium">Your Jobs</p>
            <span className="text-gray-500 text-sm">{jobs.length}</span>
          </div>
          <button onClick={() => navigate('/app/billing')} className="text-gray-500 text-sm flex items-center gap-1 hover:text-white">
            <Lock className="w-3 h-3" /> {stats?.is_subscribed ? "Manage billing" : "Upgrade to Pro"}
          </button>
        </div>

        {jobs.length === 0 ? (
          <div className="bg-[#0c0c0c] border border-dashed border-white/10 rounded-2xl p-12 text-center mb-8">
            <p className="text-gray-500 mb-4">No jobs yet. Start by adding a job posting.</p>
            <button onClick={() => navigate('/app')} className="bg-emerald-500 text-black font-medium px-6 py-3 rounded-xl hover:bg-emerald-400">
              + Add Job
            </button>
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            {jobs.map((job) => (
              <div key={job.id} className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <button onClick={() => { saveCurrentJobId(job.id); navigate('/app/questions'); }} className="text-left">
                    <h3 className="text-white font-medium hover:text-emerald-400 transition-colors">{job.job_title}</h3>
                    <p className="text-gray-500 text-sm">{job.company}</p>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="bg-white/5 text-gray-400 text-xs px-2 py-1 rounded">{job.seniority}</span>
                    <button onClick={() => handleDeleteJob(job.id)} className="w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(job.categories || ['Behavioral', 'Technical']).slice(0, 4).map((cat, index) => (
                    <span key={cat} className="text-xs px-2 py-1 rounded" style={{
                      backgroundColor: ['#f472b620', '#a78bfa20', '#4ade8020', '#f59e0b20'][index % 4],
                      color: ['#f472b6', '#a78bfa', '#4ade80', '#f59e0b'][index % 4],
                    }}>{cat}</span>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">{job.practiced_count}/{job.question_count} practiced</span>
                    <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${percent(job.practiced_count, job.question_count)}%` }} />
                    </div>
                    {job.avg_score > 0 && <span className="text-emerald-400 text-xs">Avg {job.avg_score.toFixed(1)}/10</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { saveCurrentJobId(job.id); navigate('/app/coding'); }} className="px-3 py-2 rounded-lg border border-white/10 text-gray-300 text-sm hover:bg-white/5 flex items-center gap-2">
                      <Code2 className="w-4 h-4" /> Coding
                    </button>
                    <span className="text-gray-500 text-xs">Added {new Date(job.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => navigate('/app')} className="bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> New Job
          </button>
          <button onClick={() => navigate('/packages')} className="border border-white/10 text-white py-4 rounded-xl hover:bg-white/5 flex items-center justify-center gap-2">
            <Package className="w-5 h-5" /> Company Packs
          </button>
        </div>
      </main>
    </div>
  );
};

const ProgressPage = () => {
  const [timeRange, setTimeRange] = useState('30');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/stats');
        setStats(response.data);
      } catch (loadError) {
        setError(getErrorMessage(loadError, 'Failed to load progress'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const chartData = (stats?.chart_data || []).filter((item) => {
    if (timeRange === 'all') return true;
    const boundary = new Date();
    boundary.setDate(boundary.getDate() - Number(timeRange));
    return new Date(`${item.date}T00:00:00Z`) >= boundary;
  });

  if (loading) return <LoadingScreen label="Loading your progress..." />;

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="progress" />
      <main className="pt-24 px-6 pb-12 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif text-white mb-1">Progress</h1>
            <p className="text-gray-500">Track your interview practice progress.</p>
          </div>
          <div className="flex bg-white/5 rounded-lg p-1">
            {['7', '30', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  timeRange === range ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
                }`}
              >
                {range === 'all' ? 'All' : `${range} days`}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Total Attempts</p>
              <Hash className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.total_attempts || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Interview</p>
              <FileText className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.interview_attempts || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Coding</p>
              <Code2 className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.coding_attempts || 0}</p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Average Score</p>
              <TrendingUp className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{(stats?.avg_score || 0).toFixed(1)}<span className="text-gray-500 text-lg">/10</span></p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-500 text-sm">Current Streak</p>
              <Flame className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-4xl font-serif text-white">{stats?.current_streak || 0} <span className="text-gray-500 text-lg">day{stats?.current_streak === 1 ? '' : 's'}</span></p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <p className="text-gray-500 text-sm mb-2">Best Score</p>
            <p className="text-4xl font-serif text-white">{(stats?.best_score || 0).toFixed(1)}<span className="text-gray-500 text-lg">/10</span></p>
          </div>
          <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5">
            <p className="text-gray-500 text-sm mb-2">Coding Average</p>
            <p className="text-4xl font-serif text-white">{(stats?.coding_avg_score || 0).toFixed(1)}<span className="text-gray-500 text-lg">/10</span></p>
          </div>
        </div>

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6">
          <h3 className="text-white font-medium mb-6">Score Over Time</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {chartData.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                No data yet. Start practicing!
              </div>
            ) : (
              chartData.map((item) => {
                const label = formatChartDate(item.date);
                return (
                  <div key={item.date} className="flex-1 flex flex-col items-center group">
                    <div className="relative w-full">
                      <div
                        className="w-full bg-emerald-500/80 rounded-t transition-all group-hover:bg-emerald-400"
                        style={{ height: `${(item.avg / 10) * 200}px` }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1a1a1a] px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {label}<br />Daily average: {item.avg.toFixed(1)}/10
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2">{label.split(' ')[1]}</span>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex justify-between mt-4 text-xs text-gray-500">
            <span>0</span>
            <span>Score</span>
            <span>10</span>
          </div>
        </div>
      </main>
    </div>
  );
};

const JobInputPage = () => {
  const navigate = useNavigate();
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [scrapeResult, setScrapeResult] = useState(null);

  const handleScrape = async () => {
    if (!jobUrl && !jobDescription) return;
    setIsLoading(true);
    setError('');
    setScrapeResult(null);
    try {
      const payload = jobUrl ? { url: jobUrl } : { description: jobDescription };
      const response = await apiClient.post('/scrape-job', payload);
      if (response.data.success) {
        const draftPayload = {
          url: jobUrl || '',
          description: response.data.description,
          job_title: response.data.job_title,
          company: response.data.company,
          seniority: response.data.seniority,
          suggested_categories: response.data.suggested_categories || [],
          source: response.data.source,
          word_count: response.data.word_count,
          resume_text: resumeText,
        };
        saveDraftJobData(draftPayload);
        setScrapeResult(draftPayload);
      } else {
        setError(response.data.error || 'Failed to process job input');
      }
    } catch (processError) {
      setError(getErrorMessage(processError, 'Failed to process job posting'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingResume(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/upload-resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!response.data.success) {
        throw new Error(response.data.error || 'Resume upload failed');
      }
      setResumeText(response.data.text || '');
      setResumeFileName(file.name);
    } catch (uploadError) {
      setError(getErrorMessage(uploadError, 'Failed to upload resume'));
    } finally {
      setIsUploadingResume(false);
      event.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white">1</div>
            <span className="text-white text-sm">Job Details</span>
          </div>
          <div className="w-12 h-px bg-gray-700"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-gray-700 flex items-center justify-center text-gray-500">2</div>
            <span className="text-gray-500 text-sm">Categories</span>
          </div>
          <div className="w-12 h-px bg-gray-700"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-gray-700 flex items-center justify-center text-gray-500">3</div>
            <span className="text-gray-500 text-sm">Generate</span>
          </div>
        </div>

        <h1 className="text-4xl font-serif text-white mb-2">Drop a job link.</h1>
        <h2 className="text-4xl font-serif text-emerald-400 italic mb-8">We handle the rest.</h2>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {isLoading && (
          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-2xl p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium mb-1">Scraping and analyzing the posting…</p>
                <p className="text-gray-400 text-sm mb-4">
                  We&apos;re extracting the job description, cleaning it up, and preparing role-specific categories before you generate questions.
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    'Fetching page content',
                    'Parsing title, company, and level',
                    'Preparing question categories',
                  ].map((step) => (
                    <div key={step} className="rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-gray-300">
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {scrapeResult && !isLoading && (
          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-2xl p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-2">Scrape complete</p>
                <h3 className="text-2xl text-white font-serif mb-1">{scrapeResult.job_title || 'Job captured'}</h3>
                <p className="text-gray-400">{scrapeResult.company || 'Unknown company'} · {scrapeResult.seniority || 'Mid'} level</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 text-xs text-emerald-300">
                {scrapeResult.word_count || 0} words parsed
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              {(scrapeResult.suggested_categories || []).map((category) => (
                <span key={category} className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-gray-300">
                  {category}
                </span>
              ))}
            </div>
            <div className="rounded-2xl bg-black/20 border border-white/10 p-4 mb-5">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-2">Preview</p>
              <p className="text-gray-300 text-sm leading-relaxed max-h-24 overflow-hidden">
                {scrapeResult.description}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/app/categories')}
                className="bg-emerald-500 text-black font-medium px-6 py-3 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2"
              >
                Continue to categories <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setScrapeResult(null);
                  clearDraftJobData();
                }}
                className="border border-white/10 text-white px-6 py-3 rounded-xl hover:bg-white/5"
              >
                Scrape another posting
              </button>
            </div>
          </div>
        )}

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-4 flex items-center gap-4 mb-4">
          <LinkIcon className="w-6 h-6 text-gray-500" />
          <input
            type="url"
            value={jobUrl}
            onChange={(event) => setJobUrl(event.target.value)}
            placeholder="https://linkedin.com/jobs/v..."
            className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none text-lg"
          />
          <button
            onClick={handleScrape}
            disabled={isLoading || (!jobUrl && !jobDescription)}
            className="bg-emerald-500 text-black font-medium px-6 py-3 rounded-xl hover:bg-emerald-400 flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Scrape <ArrowRight className="w-5 h-5" /></>}
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-8">Works with LinkedIn · Indeed · Greenhouse · Lever · any job URL</p>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10"></div>
          <span className="text-gray-500 text-sm">or paste the job description manually</span>
          <div className="flex-1 h-px bg-white/10"></div>
        </div>

        <div className="border-2 border-dashed border-white/10 rounded-2xl p-6 mb-6">
          <textarea
            value={jobDescription}
            onChange={(event) => setJobDescription(event.target.value)}
            placeholder="Paste the full job description here..."
            rows={6}
            className="w-full bg-transparent text-white placeholder-gray-500 focus:outline-none resize-none text-lg"
          />
        </div>

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <p className="text-white font-medium">Optional resume upload</p>
              <p className="text-gray-500 text-sm">Personalize questions to your actual background before generating them.</p>
            </div>
            <label className="bg-white/5 border border-white/10 text-white px-4 py-2 rounded-xl cursor-pointer hover:bg-white/10">
              {isUploadingResume ? 'Uploading…' : 'Upload resume'}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleResumeUpload} />
            </label>
          </div>
          {resumeFileName ? (
            <div className="rounded-xl bg-black/20 border border-white/10 p-4">
              <p className="text-emerald-400 text-sm mb-1">{resumeFileName}</p>
              <p className="text-gray-400 text-sm max-h-20 overflow-hidden">{resumeText}</p>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">PDF and DOCX supported.</p>
          )}
        </div>

        {jobDescription && (
          <button onClick={handleScrape} disabled={isLoading} className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5" /></>}
          </button>
        )}
      </main>
    </div>
  );
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const jobData = useMemo(() => getDraftJobData(), []);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState(() => getSuggestedCategories(jobData));

  useEffect(() => {
    if (!jobData.description) {
      navigate('/app');
    }
  }, [jobData.description, navigate]);

  const toggleCategory = (id) => {
    const selectedCount = categories.filter((category) => category.selected).length;
    setCategories(
      categories.map((category) => {
        if (category.id !== id) return category;
        if (!category.selected && selectedCount >= 5) return category;
        return { ...category, selected: !category.selected };
      }),
    );
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');
    try {
      const response = await apiClient.post('/jobs', {
        job_title: jobData.job_title,
        company: jobData.company,
        seniority: jobData.seniority,
        description: jobData.description,
        url: jobData.url,
        categories: categories.filter((category) => category.selected).map((category) => category.name),
        resume_text: jobData.resume_text,
      });
      if (!response.data.success || !response.data.job) {
        throw new Error(response.data.error || 'Failed to generate questions');
      }
      clearDraftJobData();
      saveCurrentJobId(response.data.job.id);
      saveCurrentQuestionId(response.data.job.questions?.[0]?.id || '');
      navigate('/app/questions');
    } catch (generateError) {
      setError(getErrorMessage(generateError, 'Failed to generate questions'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-black"><Check className="w-5 h-5" /></div>
            <span className="text-white text-sm">Job Details</span>
          </div>
          <div className="w-12 h-px bg-white/30"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white">2</div>
            <span className="text-white text-sm">Categories</span>
          </div>
          <div className="w-12 h-px bg-gray-700"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-gray-700 flex items-center justify-center text-gray-500">3</div>
            <span className="text-gray-500 text-sm">Generate</span>
          </div>
        </div>

        <h1 className="text-4xl font-serif text-white mb-2">Select Question Categories</h1>
        <p className="text-gray-400 mb-3">We detected these categories from the job posting. Select up to 5.</p>
        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-5 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <p className="text-white font-medium">{jobData.job_title || 'Untitled role'}</p>
              <p className="text-gray-500 text-sm">{jobData.company || 'Unknown company'} · {jobData.seniority || 'Mid'} level</p>
            </div>
            <div className="text-xs text-gray-500">
              {(jobData.source || 'manual').replace(':', ' via ')}
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed max-h-20 overflow-hidden">{jobData.description}</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => toggleCategory(category.id)}
              className={`p-5 rounded-2xl border text-left transition-all ${
                category.selected ? 'border-white/30 bg-white/5' : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white font-medium">{category.name}</h3>
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-gray-600" />
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${category.selected ? 'bg-white border-white' : 'border-gray-600'}`}>
                    {category.selected && <Check className="w-3 h-3 text-black" />}
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-sm">{category.description}</p>
            </button>
          ))}
        </div>

        <p className="text-gray-500 text-sm mb-4">
          {categories.filter((category) => category.selected).length} / 5 selected
        </p>

        <button
          onClick={handleGenerate}
          disabled={isGenerating || categories.filter((category) => category.selected).length === 0}
          className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</> : <>Generate Questions <Wand2 className="w-5 h-5" /></>}
        </button>
      </main>
    </div>
  );
};

const QuestionsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessmentLoading, setAssessmentLoading] = useState(true);
  const [error, setError] = useState('');
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [regeneratingQuestionId, setRegeneratingQuestionId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeView, setActiveView] = useState(location.search.includes('view=overall') ? 'overall' : 'questions');

  const loadAssessment = useCallback(async (jobId) => {
    if (!jobId) return;
    setAssessmentLoading(true);
    try {
      const response = await apiClient.get(`/jobs/${jobId}/overall-assessment`);
      if (!response.data.success || !response.data.assessment) {
        throw new Error(response.data.error || 'Failed to load overall assessment');
      }
      setAssessment(response.data.assessment);
    } catch (assessmentError) {
      setError(getErrorMessage(assessmentError, 'Failed to load overall assessment'));
    } finally {
      setAssessmentLoading(false);
    }
  }, []);

  const loadJob = useCallback(async () => {
    const jobId = getCurrentJobId();
    if (!jobId) {
      navigate('/app/dashboard');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [jobResponse, assessmentResponse] = await Promise.all([
        apiClient.get(`/jobs/${jobId}`),
        apiClient.get(`/jobs/${jobId}/overall-assessment`),
      ]);
      if (!jobResponse.data.success || !jobResponse.data.job) {
        throw new Error(jobResponse.data.error || 'Job not found');
      }
      setJob(jobResponse.data.job);
      if (!getCurrentQuestionId() && jobResponse.data.job.questions?.[0]?.id) {
        saveCurrentQuestionId(jobResponse.data.job.questions[0].id);
      }
      if (assessmentResponse.data.success && assessmentResponse.data.assessment) {
        setAssessment(assessmentResponse.data.assessment);
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Failed to load interview workspace'));
    } finally {
      setLoading(false);
      setAssessmentLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  useEffect(() => {
    setActiveView(location.search.includes('view=overall') ? 'overall' : 'questions');
  }, [location.search]);

  const handleStartPractice = (questionId) => {
    saveCurrentQuestionId(questionId);
    navigate('/app/practice');
  };

  const handleRegenerateAll = async () => {
    const jobId = getCurrentJobId();
    if (!jobId) return;
    setRegeneratingAll(true);
    setError('');
    try {
      const response = await apiClient.post(`/jobs/${jobId}/regenerate`);
      if (!response.data.success || !response.data.job) {
        throw new Error(response.data.error || 'Failed to regenerate questions');
      }
      setJob(response.data.job);
      saveCurrentQuestionId(response.data.job.questions?.[0]?.id || "");
      await loadAssessment(jobId);
    } catch (regenerateError) {
      setError(getErrorMessage(regenerateError, 'Failed to regenerate questions'));
    } finally {
      setRegeneratingAll(false);
    }
  };

  const handleRegenerateQuestion = async (questionId) => {
    const jobId = getCurrentJobId();
    if (!jobId) return;
    setRegeneratingQuestionId(questionId);
    setError('');
    try {
      const response = await apiClient.post(`/jobs/${jobId}/questions/${questionId}/regenerate`);
      if (!response.data.success || !response.data.question) {
        throw new Error(response.data.error || 'Failed to regenerate question');
      }
      await loadJob();
    } catch (regenerateError) {
      setError(getErrorMessage(regenerateError, 'Failed to regenerate question'));
    } finally {
      setRegeneratingQuestionId("");
    }
  };

  const filteredQuestions = useMemo(() => {
    const questions = job?.questions || [];
    return questions.filter((question) => {
      const matchesSearch = !searchQuery.trim() || `${question.text} ${(question.categories || []).join(' ')}`.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter =
        statusFilter === 'all' ||
        (statusFilter === 'practiced' && question.answered) ||
        (statusFilter === 'unpracticed' && !question.answered) ||
        (statusFilter === 'easy' && question.difficulty === 'Easy') ||
        (statusFilter === 'hard' && question.difficulty === 'Hard');
      return matchesSearch && matchesFilter;
    });
  }, [job, searchQuery, statusFilter]);

  if (loading) return <LoadingScreen label="Loading your interview workspace..." />;

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-2">Interview workspace</p>
            <h1 className="text-3xl font-serif text-white">{job?.job_title || 'Interview Questions'}</h1>
            <p className="text-gray-500 text-sm">{job?.company} · {job?.questions?.length || 0} questions · {job?.practiced_count || 0} practiced</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => navigate('/app/coding')} className="border border-white/10 px-4 py-2 rounded-lg text-gray-300 hover:text-white text-sm flex items-center gap-2">
              <Code2 className="w-4 h-4" /> Coding round
            </button>
            <button onClick={handleRegenerateAll} disabled={regeneratingAll} className="border border-white/10 px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm disabled:opacity-60 flex items-center gap-2">
              <RefreshCcw className="w-4 h-4" /> {regeneratingAll ? 'Regenerating…' : 'Regenerate all'}
            </button>
            <button onClick={() => navigate('/app/dashboard')} className="border border-white/10 px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">
              ← Back
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <div className="grid xl:grid-cols-[1.2fr_0.8fr] gap-6 mb-6">
          <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex bg-white/5 rounded-lg p-1">
                {[
                  ['questions', 'Questions'],
                  ['overall', 'Overall assessment'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => {
                      setActiveView(value);
                      navigate(value === 'overall' ? '/app/questions?view=overall' : '/app/questions');
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      activeView === value ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span className="px-3 py-1 rounded-full border border-white/10 bg-white/5">
                  Completion {assessment?.completion_rate || 0}%
                </span>
                <span className="px-3 py-1 rounded-full border border-white/10 bg-white/5">
                  Readiness {assessment?.readiness_score?.toFixed?.(1) || '0.0'}/10
                </span>
              </div>
            </div>

            {activeView === 'overall' ? (
              assessmentLoading ? (
                <div className="rounded-2xl border border-white/10 bg-black/20 p-10 text-center">
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Calculating overall assessment...</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                      <p className="text-gray-500 text-sm mb-2">Readiness</p>
                      <p className="text-white text-3xl font-serif">{assessment?.readiness_score?.toFixed?.(1) || '0.0'}</p>
                      <p className="text-emerald-400 text-sm mt-2">{assessment?.readiness_label || 'Not started'}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                      <p className="text-gray-500 text-sm mb-2">Interview average</p>
                      <p className="text-white text-3xl font-serif">{assessment?.interview_average_score?.toFixed?.(1) || '0.0'}</p>
                      <p className="text-gray-500 text-sm mt-2">Across {assessment?.practiced_questions || 0} practiced questions</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                      <p className="text-gray-500 text-sm mb-2">Coding average</p>
                      <p className="text-white text-3xl font-serif">{assessment?.coding?.average_score?.toFixed?.(1) || '0.0'}</p>
                      <p className="text-gray-500 text-sm mt-2">{assessment?.coding?.attempts || 0} adaptive coding round(s)</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white font-medium">Loop completion</p>
                      <p className="text-gray-500 text-sm">{assessment?.practiced_questions || 0}/{assessment?.question_count || 0} questions</p>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                      <div className="h-full bg-emerald-500" style={{ width: `${assessment?.completion_rate || 0}%` }} />
                    </div>
                    <p className="text-gray-500 text-sm">
                      {assessment?.remaining_questions ? `${assessment.remaining_questions} question(s) left before the verbal loop is complete.` : 'You completed the current verbal interview loop.'}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                      <p className="text-white font-medium mb-4">Strongest categories</p>
                      <div className="space-y-3">
                        {(assessment?.strongest_categories || []).map((item) => (
                          <div key={item.category} className="flex items-center justify-between">
                            <span className="text-gray-300">{item.category}</span>
                            <span className="text-emerald-400 text-sm">{item.average_score.toFixed(1)}/10</span>
                          </div>
                        ))}
                        {!(assessment?.strongest_categories || []).length && <p className="text-gray-500 text-sm">Practice a few questions to unlock category trends.</p>}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                      <p className="text-white font-medium mb-4">Needs work</p>
                      <div className="space-y-3">
                        {(assessment?.weakest_categories || []).map((item) => (
                          <div key={item.category} className="flex items-center justify-between">
                            <span className="text-gray-300">{item.category}</span>
                            <span className="text-yellow-400 text-sm">{item.average_score.toFixed(1)}/10</span>
                          </div>
                        ))}
                        {!(assessment?.weakest_categories || []).length && <p className="text-gray-500 text-sm">No weak spots yet — you just need more data.</p>}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                    <p className="text-white font-medium mb-4">Recommended next steps</p>
                    <div className="space-y-3">
                      {(assessment?.next_steps || []).map((step) => (
                        <div key={step} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-emerald-400 mt-0.5" />
                          <p className="text-gray-300">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search questions or categories"
                    className="flex-1 min-w-[220px] bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none"
                  />
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['all', 'All'],
                      ['unpracticed', 'Unpracticed'],
                      ['practiced', 'Practiced'],
                      ['easy', 'Easy'],
                      ['hard', 'Hard'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        onClick={() => setStatusFilter(value)}
                        className={`px-3 py-2 rounded-lg text-sm border ${
                          statusFilter === value ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-white/10 text-gray-500 hover:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {filteredQuestions.map((question, index) => (
                    <div key={question.id || index} className="bg-black/20 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors">
                      <div className="flex items-start gap-4">
                        <span className="text-gray-500 text-sm mt-1">{index + 1}.</span>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                            <p className="text-white flex-1">{question.text}</p>
                            <span className={`text-xs px-2 py-1 rounded ${
                              question.answered ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-gray-400'
                            }`}>
                              {question.answered ? 'Practiced' : 'Queued'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {question.categories?.map((cat) => (
                              <span key={cat} className="bg-white/5 text-gray-400 text-xs px-2 py-1 rounded">{cat}</span>
                            ))}
                            <span className={`text-xs px-2 py-1 rounded ${
                              question.difficulty === 'Easy' ? 'bg-emerald-500/20 text-emerald-400' :
                              question.difficulty === 'Hard' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>{question.difficulty || 'Medium'}</span>
                            {question.answered && <span className="text-emerald-400 text-xs">✓ {question.score?.toFixed(1)}/10</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <button
                            onClick={() => handleRegenerateQuestion(question.id)}
                            disabled={regeneratingQuestionId === question.id}
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 text-sm hover:bg-white/10 disabled:opacity-60 flex items-center gap-2"
                          >
                            <RefreshCcw className="w-4 h-4" /> {regeneratingQuestionId === question.id ? 'Refreshing…' : 'Refresh'}
                          </button>
                          <button onClick={() => handleStartPractice(question.id)} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10">
                            Practice
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!filteredQuestions.length && <p className="text-gray-500 text-sm">No questions match your current filters.</p>}
                </div>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-3">Full loop</p>
              <h2 className="text-white text-xl font-medium mb-2">Verbal + coding, in one workspace</h2>
              <p className="text-gray-400 text-sm mb-5">Practice behavioral and technical stories, then pressure-test implementation with an adaptive coding round tied to the same job.</p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="text-gray-500 text-sm mb-1">Questions practiced</p>
                  <p className="text-white text-3xl font-serif">{assessment?.practiced_questions || 0}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="text-gray-500 text-sm mb-1">Coding rounds</p>
                  <p className="text-white text-3xl font-serif">{assessment?.coding?.attempts || 0}</p>
                </div>
              </div>
              <button onClick={() => navigate('/app/coding')} className="w-full bg-emerald-500 text-black font-medium py-3 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2">
                <Code2 className="w-4 h-4" /> Start adaptive coding
              </button>
            </div>

            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-3">Current job snapshot</p>
              <div className="space-y-3">
                {(job?.categories || []).map((category) => (
                  <div key={category} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                    <span className="text-gray-300">{category}</span>
                    <span className="text-gray-500 text-sm">Category</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};


const CodingAssessmentPage = () => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [runResult, setRunResult] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const submissionRef = useRef(null);

  const loadOrGenerate = useCallback(async () => {
    const jobId = getCurrentJobId();
    if (!jobId) {
      navigate('/app/dashboard');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const jobResponse = await apiClient.get(`/jobs/${jobId}`);
      if (!jobResponse.data.success || !jobResponse.data.job) {
        throw new Error(jobResponse.data.error || 'Job not found');
      }
      setJob(jobResponse.data.job);

      const challengeResponse = await apiClient.post('/coding/generate', { job_id: jobId });
      if (!challengeResponse.data.success || !challengeResponse.data.challenge) {
        throw new Error(challengeResponse.data.error || 'Could not generate coding assessment');
      }
      setChallenge(challengeResponse.data.challenge);
      const draftCode = loadCodingDraft(challengeResponse.data.challenge.id);
      setCode(draftCode || challengeResponse.data.challenge.starter_code || '');
      setRunResult(null);
      setSubmission(null);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Could not load coding assessment'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadOrGenerate();
  }, [loadOrGenerate]);

  useEffect(() => {
    if (!challenge?.id) return;
    saveCodingDraft(challenge.id, code);
  }, [challenge?.id, code]);

  useEffect(() => {
    if (!submission) return;
    submissionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [submission]);

  const handleCopyFeedback = async () => {
    if (!submission?.feedback) return;
    try {
      await copyText(submission.feedback);
      setCopyMessage('Feedback copied');
      window.setTimeout(() => setCopyMessage(''), 2000);
    } catch (copyError) {
      setError(getErrorMessage(copyError, 'Could not copy feedback'));
    }
  };

  const handleGenerateNext = async () => {
    setGenerating(true);
    await loadOrGenerate();
    setGenerating(false);
  };

  const handleRun = useCallback(async () => {
    if (!challenge) return;
    setRunning(true);
    setError('');
    try {
      const response = await apiClient.post('/coding/run', {
        challenge_id: challenge.id,
        code,
      });
      if (!response.data.success) {
        throw new Error(response.data.error || 'Run failed');
      }
      setRunResult(response.data);
    } catch (runError) {
      setError(getErrorMessage(runError, 'Could not run code'));
    } finally {
      setRunning(false);
    }
  }, [challenge, code]);

  const handleSubmitSolution = useCallback(async () => {
    if (!challenge) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await apiClient.post('/coding/submit', {
        challenge_id: challenge.id,
        code,
      });
      if (!response.data.success) {
        throw new Error(response.data.error || 'Submission failed');
      }
      setSubmission(response.data);
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'Could not submit coding solution'));
    } finally {
      setSubmitting(false);
    }
  }, [challenge, code]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || !challenge) return;
      if (event.shiftKey && event.key === 'Enter') {
        event.preventDefault();
        handleRun();
      } else if (!event.shiftKey && event.key === 'Enter') {
        event.preventDefault();
        handleSubmitSolution();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [challenge, handleRun, handleSubmitSolution]);

  if (loading) return <LoadingScreen label="Generating adaptive coding challenge..." />;

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-emerald-400 text-xs tracking-[0.25em] uppercase mb-2">Coding assessment</p>
            <h1 className="text-3xl font-serif text-white">{challenge?.title || 'Adaptive coding round'}</h1>
            <p className="text-gray-500 text-sm">
              {job?.job_title} · {challenge?.language} · {challenge?.difficulty} · {challenge?.topic}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/app/questions')} className="border border-white/10 px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">
              ← Back to questions
            </button>
            <button onClick={handleGenerateNext} disabled={generating} className="border border-white/10 px-4 py-2 rounded-lg text-gray-300 hover:text-white text-sm disabled:opacity-60 flex items-center gap-2">
              <BrainCircuit className="w-4 h-4" /> {generating ? 'Generating…' : 'Next adaptive challenge'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
          </div>
        )}

        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6">
          <div className="space-y-6">
            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-3">Prompt</p>
              <p className="text-gray-200 whitespace-pre-line leading-relaxed">{challenge?.prompt}</p>
            </div>

            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-4">Visible tests</p>
              <div className="space-y-3">
                {(challenge?.visible_tests || []).map((test) => (
                  <div key={test.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <p className="text-white text-sm mb-1">{test.id}</p>
                    <p className="text-gray-400 text-sm mb-1">Input: <span className="text-gray-200">{JSON.stringify(test.input)}</span></p>
                    <p className="text-gray-400 text-sm mb-1">Expected: <span className="text-gray-200">{JSON.stringify(test.expected)}</span></p>
                    {test.explanation && <p className="text-gray-500 text-xs">{test.explanation}</p>}
                  </div>
                ))}
              </div>
            </div>

            {submission && (
              <div ref={submissionRef} className="bg-[#0c0c0c] border border-emerald-500/20 rounded-3xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] mb-2">Adaptive result</p>
                    <p className="text-white text-4xl font-serif">{submission.overall_score?.toFixed(1)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-lg">{submission.passed}/{submission.total} tests passed</p>
                    <p className="text-gray-500 text-sm">Next target: {submission.next_difficulty}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    ['Correctness', submission.correctness_score],
                    ['Code quality', submission.code_quality_score],
                    ['Efficiency', submission.efficiency_score],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                      <p className="text-gray-500 text-sm mb-1">{label}</p>
                      <p className="text-white text-2xl font-serif">{Number(value || 0).toFixed(1)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-gray-300 whitespace-pre-line mb-4">{submission.feedback}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-gray-500 text-sm">Scored via {formatAiProvider(submission.provider)}</p>
                    {copyMessage && <p className="text-emerald-400 text-xs mt-1">{copyMessage}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={handleCopyFeedback} className="px-4 py-2 rounded-xl border border-white/10 text-white hover:bg-white/5 flex items-center gap-2">
                      <Copy className="w-4 h-4" /> Copy feedback
                    </button>
                    <button onClick={() => navigate('/app/questions?view=overall')} className="px-4 py-2 rounded-xl border border-white/10 text-white hover:bg-white/5">
                      View overall assessment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-medium">Editor</p>
                  <p className="text-gray-500 text-sm">Current language: {challenge?.language}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={handleRun} disabled={running} className="px-4 py-2 rounded-xl border border-white/10 text-white hover:bg-white/5 disabled:opacity-60 flex items-center gap-2">
                    <Play className="w-4 h-4" /> {running ? 'Running…' : 'Run visible tests'}
                  </button>
                  <button onClick={handleSubmitSolution} disabled={submitting} className="px-4 py-2 rounded-xl bg-emerald-500 text-black font-medium hover:bg-emerald-400 disabled:opacity-60">
                    {submitting ? 'Submitting…' : 'Submit solution'}
                  </button>
                </div>
              </div>
              <p className="text-gray-500 text-sm mb-3">Drafts autosave for each coding challenge. Press Shift + Ctrl/Cmd + Enter to run tests, or Ctrl/Cmd + Enter to submit.</p>
              {submission && (
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 mb-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <p className="text-emerald-300 font-medium">Submission graded successfully</p>
                    <button onClick={() => submissionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-emerald-300 hover:text-emerald-200">
                      Jump to feedback ↑
                    </button>
                  </div>
                  <p className="text-gray-300 text-sm line-clamp-3">{submission.feedback}</p>
                </div>
              )}
              <textarea
                value={code}
                onChange={(event) => setCode(event.target.value)}
                spellCheck={false}
                rows={20}
                className="w-full bg-black/30 border border-white/10 rounded-2xl p-4 text-gray-200 font-mono text-sm focus:outline-none resize-y"
              />
            </div>

            <div className="bg-[#0c0c0c] border border-white/10 rounded-3xl p-6">
              <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mb-4">Run results</p>
              {runResult ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-white">{runResult.passed}/{runResult.total} visible tests passed</p>
                    <p className="text-gray-500 text-sm">{runResult.runtime_seconds?.toFixed(2)}s</p>
                  </div>
                  <div className="space-y-3">
                    {(runResult.results || []).map((result) => (
                      <div key={result.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white text-sm">{result.id}</p>
                          <span className={`text-xs ${result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                            {result.passed ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                        {result.error ? (
                          <p className="text-red-300 text-sm">{result.error}</p>
                        ) : (
                          <>
                            <p className="text-gray-400 text-sm">Output: <span className="text-gray-200">{JSON.stringify(result.output)}</span></p>
                            <p className="text-gray-400 text-sm">Expected: <span className="text-gray-200">{JSON.stringify(result.expected)}</span></p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm">Run the visible tests to validate your solution before final submission.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const PracticePage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('voice');
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [typedAnswer, setTypedAnswer] = useState('');
  const [gradingDepth, setGradingDepth] = useState('harsh');
  const [gradeAs, setGradeAs] = useState('mid');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const recognitionRef = useRef(null);

  const currentQuestionId = getCurrentQuestionId();
  const currentQuestion = job?.questions?.find((question) => question.id === currentQuestionId) || null;

  useEffect(() => {
    const load = async () => {
      const jobId = getCurrentJobId();
      if (!jobId) {
        navigate('/app/dashboard');
        return;
      }
      try {
        const response = await apiClient.get(`/jobs/${jobId}`);
        if (!response.data.success || !response.data.job) {
          throw new Error(response.data.error || 'Job not found');
        }
        setJob(response.data.job);
        const stored = getPracticeState();
        setTypedAnswer(stored.answer || '');
        setTranscript(stored.mode === 'voice' ? (stored.answer || '') : '');
        setMode(stored.mode || 'voice');
        setGradingDepth(stored.grading_depth || 'harsh');
        setGradeAs(stored.grade_as || 'mid');
      } catch {
        navigate('/app/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          if (event.results[index].isFinal) finalTranscript += `${event.results[index][0].transcript} `;
        }
        setTranscript((previous) => previous + finalTranscript);
      };
    }
    return () => recognitionRef.current?.stop();
  }, []);

  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      setTranscript('');
      recognitionRef.current?.start();
      setIsRecording(true);
    }
  };

  const answer = mode === 'voice' ? transcript : typedAnswer;

  useEffect(() => {
    if (!currentQuestion) return;
    savePracticeState({
      answer,
      question_id: currentQuestion.id,
      grading_depth: gradingDepth,
      grade_as: gradeAs,
      mode,
    });
  }, [answer, currentQuestion, gradingDepth, gradeAs, mode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && answer.trim() && currentQuestion) {
        event.preventDefault();
        savePracticeState({
          answer,
          question_id: currentQuestion.id,
          grading_depth: gradingDepth,
          grade_as: gradeAs,
          mode,
        });
        navigate('/app/result');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [answer, currentQuestion, gradeAs, gradingDepth, mode, navigate]);

  const speakQuestion = () => {
    if (!("speechSynthesis" in window) || !currentQuestion?.text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(currentQuestion.text);
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  };

  const toggleFocusMode = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      console.error("Unable to toggle fullscreen mode", error);
    }
  };

  const handleSubmit = () => {
    if (!answer.trim() || !currentQuestion) return;
    savePracticeState({
      answer,
      question_id: currentQuestion.id,
      grading_depth: gradingDepth,
      grade_as: gradeAs,
      mode,
    });
    navigate('/app/result');
  };

  if (loading) return <LoadingScreen label="Preparing your practice session..." />;

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">No questions loaded</p>
          <button onClick={() => navigate('/app/dashboard')} className="text-emerald-400">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/app/questions')} className="text-gray-500 text-sm hover:text-white flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to questions
          </button>
          <span className="text-gray-500 text-sm">
            {(job.questions.findIndex((question) => question.id === currentQuestion.id) || 0) + 1} / {job.questions.length}
          </span>
        </div>

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <p className="text-white text-xl font-medium leading-relaxed flex-1">{currentQuestion.text}</p>
            <button onClick={speakQuestion} className="ml-4 text-gray-500 hover:text-white"><Volume2 className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-2 mt-4">
            {currentQuestion.categories?.slice(0, 2).map((cat) => (
              <span key={cat} className="bg-white/5 text-gray-400 text-xs px-3 py-1 rounded-lg">{cat}</span>
            ))}
            <span className={`px-2 py-0.5 rounded text-xs ${
              currentQuestion.difficulty === 'Easy' ? 'bg-emerald-500/20 text-emerald-400' :
              currentQuestion.difficulty === 'Hard' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>{currentQuestion.difficulty || 'Medium'}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <button onClick={() => setMode('voice')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${mode === 'voice' ? 'bg-white/5 border-white/20 text-white' : 'border-white/10 text-gray-500'}`}>
              <Mic className="w-4 h-4" /> Voice
            </button>
            <button onClick={() => setMode('text')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${mode === 'text' ? 'bg-white/5 border-white/20 text-white' : 'border-white/10 text-gray-500'}`}>
              <span>T</span> Text
            </button>
          </div>
          <button onClick={toggleFocusMode} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 text-gray-400 hover:text-white">
            <Maximize2 className="w-4 h-4" /> Focus Mode
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-gray-500 text-sm mb-2 block">Grading Depth</label>
            <select value={gradingDepth} onChange={(event) => setGradingDepth(event.target.value)} className="w-full bg-[#0c0c0c] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none">
              <option value="harsh">Harsh 🔥 (Recommended)</option>
              <option value="balanced">Balanced</option>
              <option value="gentle">Gentle</option>
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-sm mb-2 block">Grade As</label>
            <select value={gradeAs} onChange={(event) => setGradeAs(event.target.value)} className="w-full bg-[#0c0c0c] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none">
              <option value="junior">Junior</option>
              <option value="mid">Mid-Level</option>
              <option value="senior">Senior</option>
            </select>
          </div>
        </div>

        {mode === 'voice' ? (
          <div className="mb-6">
            <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 min-h-[150px] mb-4">
              {transcript ? <p className="text-white">{transcript}</p> : <p className="text-gray-500">Your answer will appear here...</p>}
            </div>
            <div className="flex justify-end text-gray-500 text-sm mb-4">{answer.length} / 5,000</div>
            <button onClick={toggleRecording} className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-[#1a1a1a] border border-white/10 text-white hover:bg-[#222]'}`}>
              {isRecording ? <><Pause className="w-5 h-5" /> Stop Recording</> : <><Mic className="w-5 h-5" /> Start Recording</>}
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <textarea value={typedAnswer} onChange={(event) => setTypedAnswer(event.target.value)} placeholder="Type your answer..." rows={8} className="w-full bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 text-white placeholder-gray-500 focus:outline-none resize-none" />
            <div className="flex justify-end text-gray-500 text-sm mt-2">{answer.length} / 5,000</div>
          </div>
        )}

        <p className="text-gray-500 text-sm mb-3">Autosaves as you practice. Press Ctrl/Cmd + Enter to submit instantly.</p>
        <button onClick={handleSubmit} disabled={!answer.trim()} className="w-full bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2 disabled:opacity-50">
          Submit Answer <ChevronRight className="w-5 h-5" />
        </button>
      </main>
    </div>
  );
};

const ResultPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [feedback, setFeedback] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paywallMessage, setPaywallMessage] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    const getFeedback = async () => {
      const practiceState = getPracticeState();
      const jobId = getCurrentJobId();
      if (!jobId || !practiceState.answer || !practiceState.question_id) {
        navigate('/app/dashboard');
        return;
      }

      try {
        const jobResponse = await apiClient.get(`/jobs/${jobId}`);
        if (!jobResponse.data.success || !jobResponse.data.job) {
          throw new Error(jobResponse.data.error || 'Job not found');
        }
        const loadedJob = jobResponse.data.job;
        setJob(loadedJob);
        const currentQuestion = loadedJob.questions.find((question) => question.id === practiceState.question_id);
        if (!currentQuestion) {
          throw new Error('Question not found');
        }
        const response = await apiClient.post('/attempts/evaluate', {
          job_id: jobId,
          question_id: currentQuestion.id,
          question: currentQuestion.text,
          answer: practiceState.answer,
          job_title: loadedJob.job_title,
          categories: currentQuestion.categories || [],
          grading_depth: practiceState.grading_depth || 'balanced',
          grade_as: practiceState.grade_as || 'mid',
        });
        if (!response.data.success) {
          throw new Error(response.data.error || 'Evaluation failed');
        }
        setFeedback({ ...response.data, questionId: currentQuestion.id });
      } catch (loadError) {
        if (getErrorDetail(loadError)?.code === "subscription_required") {
          setPaywallMessage(getErrorMessage(loadError, 'Upgrade to keep seeing AI result assessments'));
        } else {
          setError(getErrorMessage(loadError, 'Could not evaluate your answer'));
        }
      } finally {
        setLoading(false);
      }
    };
    getFeedback();
  }, [navigate]);

  const handleCopyFeedback = async () => {
    if (!feedback?.feedback) return;
    try {
      await copyText(feedback.feedback);
      setCopyMessage('Feedback copied');
      window.setTimeout(() => setCopyMessage(''), 2000);
    } catch (copyError) {
      setError(getErrorMessage(copyError, 'Could not copy feedback'));
    }
  };

  const handleNext = () => {
    if (!job || !feedback) {
      navigate('/app/dashboard');
      return;
    }
    const currentIndex = job.questions.findIndex((question) => question.id === feedback.questionId);
    const nextQuestion = job.questions[currentIndex + 1];
    clearPracticeState();
    if (nextQuestion) {
      saveCurrentQuestionId(nextQuestion.id);
      navigate('/app/practice');
    } else {
      navigate('/app/questions?view=overall');
    }
  };

  if (loading) return <LoadingScreen label="Analyzing your answer..." />;

  if (paywallMessage) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <AppNavbar activeTab="dashboard" />
        <main className="pt-24 px-6 pb-12 max-w-2xl mx-auto">
          <div className="bg-[#0c0c0c] border border-emerald-500/20 rounded-3xl p-8 text-center">
            <div className="w-16 h-16 rounded-3xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
              <Crown className="w-8 h-8 text-emerald-300" />
            </div>
            <p className="text-emerald-400 text-sm tracking-[0.25em] uppercase mb-3">Upgrade required</p>
            <h1 className="text-3xl font-serif text-white mb-3">You used your 2 free result assessments.</h1>
            <p className="text-gray-400 mb-8">{paywallMessage}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => navigate('/app/billing')} className="bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400">
                Unlock with Stripe
              </button>
              <button onClick={() => navigate('/app/practice')} className="border border-white/10 text-white py-4 rounded-xl hover:bg-white/5">
                Edit answer
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              Signed in as {auth.user?.email}. Billing is account-based, so your upgrade will unlock future assessments everywhere.
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="max-w-xl w-full bg-[#0c0c0c] border border-red-500/20 rounded-2xl p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-white mb-3">Evaluation failed</h1>
          <p className="text-red-300 mb-6">{error}</p>
          <button onClick={() => navigate('/app/practice')} className="bg-emerald-500 text-black font-medium px-6 py-3 rounded-xl hover:bg-emerald-400">
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <AppNavbar activeTab="dashboard" />
      <main className="pt-24 px-6 pb-12 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-emerald-400 text-sm tracking-widest uppercase mb-2">
            AI Scored via {formatAiProvider(feedback?.provider)}
          </p>
          <h1 className="text-6xl font-serif text-white mb-2">{feedback?.overall_score?.toFixed(1)}</h1>
          <p className="text-gray-400">{feedback?.overall_score >= 8 ? 'Excellent!' : feedback?.overall_score >= 7 ? 'Good job!' : 'Keep practicing'}</p>
        </div>

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 mb-6">
          <p className="text-gray-500 text-sm tracking-widest uppercase mb-6">Breakdown</p>
          {[
            { label: 'Structure (STAR)', score: feedback?.structure_score },
            { label: 'Impact', score: feedback?.impact_score },
            { label: 'Clarity', score: feedback?.clarity_score },
            { label: 'Role fit', score: feedback?.role_fit_score },
          ].map((item) => (
            <div key={item.label} className="mb-4">
              <div className="flex justify-between mb-1"><span className="text-white">{item.label}</span><span className="text-gray-400">{item.score?.toFixed(1)}</span></div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full ${item.score >= 8 ? 'bg-emerald-500' : item.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${item.score * 10}%` }} />
              </div>
            </div>
          ))}
        </div>

        {feedback?.filler_word_count > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-6 mb-6">
            <p className="text-yellow-400 font-medium mb-3">⚠️ Filler Words: {feedback.filler_word_count}</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(feedback.filler_words || {}).map(([word, count]) => (
                <span key={word} className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm">&quot;{word}&quot; × {count}</span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-gray-500 text-sm tracking-widest uppercase">AI Feedback</p>
            <button onClick={handleCopyFeedback} className="px-3 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 flex items-center gap-2 text-sm">
              <Copy className="w-4 h-4" /> Copy feedback
            </button>
          </div>
          <p className="text-white whitespace-pre-line">{feedback?.feedback}</p>
          {copyMessage && <p className="text-emerald-400 text-sm mt-4">{copyMessage}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate('/app/practice')} className="border border-white/10 text-white py-4 rounded-xl hover:bg-white/5">Try again</button>
          <button onClick={handleNext} className="bg-emerald-500 text-black font-medium py-4 rounded-xl hover:bg-emerald-400 flex items-center justify-center gap-2">
            {job?.questions?.findIndex((question) => question.id === feedback?.questionId) + 1 < (job?.questions?.length || 0) ? 'Next question' : 'Finish'} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </main>
    </div>
  );
};

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/packages" element={<RequireAuth><PackagesPage /></RequireAuth>} />
        <Route path="/feedback" element={<RequireAuth><FeedbackPage /></RequireAuth>} />
        <Route path="/app" element={<RequireAuth><JobInputPage /></RequireAuth>} />
        <Route path="/app/categories" element={<RequireAuth><CategoriesPage /></RequireAuth>} />
        <Route path="/app/questions" element={<RequireAuth><QuestionsListPage /></RequireAuth>} />
        <Route path="/app/coding" element={<RequireAuth><CodingAssessmentPage /></RequireAuth>} />
        <Route path="/app/practice" element={<RequireAuth><PracticePage /></RequireAuth>} />
        <Route path="/app/result" element={<RequireAuth><ResultPage /></RequireAuth>} />
        <Route path="/app/billing" element={<RequireAuth><BillingPage /></RequireAuth>} />
        <Route path="/app/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
        <Route path="/app/history" element={<RequireAuth><HistoryPage /></RequireAuth>} />
        <Route path="/app/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/app/progress" element={<RequireAuth><ProgressPage /></RequireAuth>} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}
